import * as React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  ActionIcon,
  Group,
  useMantineTheme,
  Flex,
  Stack,
  Divider,
} from "@mantine/core";
import dayjs from "dayjs";

import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { IconPlus, IconTrash, IconX } from "@tabler/icons-react";

import {
  PolicyAdapterSchema,
  PolicyAdapterType,
} from "@supportinitiative/common";

import {
  CSelect,
  CTextInput,
  CDatePicker,
  CJsonInput,
  CTextarea,
} from "@supportinitiative/react-hook-form-mantine";

import { styles } from "../../styles";
import { generateApiKey, postAdapter } from "../../api";
import { AuditType, IDispatchAction } from "../../types";
import { getActionOptions } from "../../Adapter/AdapterUtils";
import { UserContext } from "../../misc/UserContext";
import { useActiveRoles, useAdapter } from "../../hooks";
import FormSubmitError from "../../ui/Error/FormSubmitError";
import FormStateError from "../../ui/Error/FormStateError";
import Success from "../../ui/Success";
import Audit from "../../ui/Audit/Audit";
import { getId, showError } from "../../utilities";

type actionTypes =
  | "CURRENT-ACTION"
  | "ACTION-OPTIONS"
  | "SET-VALUE"
  | "SET-SUBMITTING"
  | "SET-SUCCESS"
  | "SET-ERROR";

export const PolicyCVMContext = React.createContext<{
  state: any;
  dispatch: React.Dispatch<IDispatchAction<actionTypes>>;
}>({
  state: {},
  dispatch: () => {},
});

const initialState = {
  currentAction: "NEW",
  entityId: "",
  actionOptions: {},
  setValue: () => {},
  submitting: false,
  error: "",
  successMessage: "",
};

function reducer(state: any, action: IDispatchAction<actionTypes>) {
  switch (action.type) {
    case "CURRENT-ACTION":
      return { ...state, currentAction: action.value };
    case "ACTION-OPTIONS":
      return { ...state, actionOptions: action.value };
    case "SET-VALUE":
      return { ...state, setValue: action.value };
    case "SET-SUBMITTING":
      return { ...state, submitting: action.value };
    case "SET-SUCCESS":
      return { ...state, successMessage: action.value };
    case "SET-ERROR":
      return { ...state, error: action.value };
    default:
      return state;
  }
}

function get30DaysPlus() {
  var date = new Date(); // Now
  date.setDate(date.getDate() + 30);
  return dayjs(date).format("YYYY-MM-DD");
}

export default function PolicyCVM() {
  // let [dataset, setPolicy] = React.useState<PolicyAdapterType>();

  let [audit, setAudit] = React.useState<AuditType | undefined>();

  // let [currentAction, setCurrentAction] = React.useState("new");
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId") as string;
  const { state: applicationState } = React.useContext(UserContext);
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  const { data: dataset } = useAdapter(entityId, id, "POLICY");
  // let [newPolicy, setNewPolicy] = React.useState<PolicyAdapterType | undefined>();
  const [policyFormState, policyFormDispatch] = React.useReducer(reducer, {
    ...initialState,
    actionOptions: getActionOptions("new", dataset, dataset, applicationState),
  });
  // console.log("dataset in component", dataset);

  const { data: activeRoles } = useActiveRoles(entityId);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PolicyAdapterType>({
    defaultValues: {
      name: "",
      displayName: "",
      description: "",
      type: "POLICY",
      definition: {},
    },
    criteriaMode: "all",
    resolver: yupResolver(PolicyAdapterSchema),
  });

  const onOk = () => {
    navigate(`/app/policies?entityId=${entityId}`);
  };

  React.useEffect(() => {
    if (dataset) {
      policyFormDispatch({ type: "CURRENT-ACTION", value: "VIEW" });
      policyFormDispatch({
        type: "ACTION-OPTIONS",
        value: getActionOptions("view", dataset, dataset, applicationState),
      });

      // setColumns(dataset.definition.columns || []);

      setAudit({
        status: dataset.status,
        authStatus: dataset.authStatus,
        usableStatus: dataset.usableStatus,
        makerId: dataset.makerId,
        makerDate: dataset.makerDate,
        modificationNo: dataset.modificationNo,
        checkerId: dataset.checkerId,
        checkerDate: dataset.checkerDate,
      });

      reset(dataset);
    }
  }, [dataset]);

  const onSubmit = async (data: any) => {
    try {
      policyFormDispatch({
        type: "SET-SUBMITTING",
        value: true,
      });
      policyFormDispatch({
        type: "SET-ERROR",
        value: "",
      });
      let payload = {
        ...data,
        entityId,
        metaData: {
          action: policyFormState.currentAction,
        },
      };
      await postAdapter(entityId, payload);
      // setError("Form", "Unable to Submit");
      policyFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      queryClient.invalidateQueries(["dataset", entityId, id]);
      queryClient.invalidateQueries(["policies", entityId]);
      policyFormDispatch({
        type: "SET-SUCCESS",
        value: "Policy saved successfully",
      });
    } catch (error: any) {
      // console.log("post catch", error);
      // console.log("post catch", error?.response?.data?.message);
      policyFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      policyFormDispatch({
        type: "SET-ERROR",
        value: error?.response?.data?.message || "Unable to save policy",
      });
    }
  };

  return (
    <PolicyCVMContext.Provider
      value={{ state: policyFormState, dispatch: policyFormDispatch }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ marginTop: theme.spacing.lg }}
        noValidate
      >
        <div id="header">
          <Grid
            align="flex-end"
            gutter={"xs"}
            mt="xs"
            mb="xs"
            // mb="xs"
            style={{
              display: "flex",
              // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
            }}
            columns={12}
          >
            <Grid.Col span={3}>
              <Flex align={"end"} gap={theme.spacing.xs}>
                <Stack gap={0}>
                  <Title order={3} c="dark">
                    Policy
                  </Title>
                </Stack>
              </Flex>
            </Grid.Col>
          </Grid>
        </div>

        <Divider size="sm" mb="xs" color="dark.2" />

        <div id="body">
          <Grid align="flex-start" gutter={"xs"} mb="lg">
            <Grid.Col span={4}>
              <CTextInput
                name="name"
                control={control}
                label="Name"
                aria-label="Name"
                required
                showErrorText={false}
                disabled={policyFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name="displayName"
                control={control}
                label="Display Name"
                aria-label="Display Name"
                required
                showErrorText={false}
                disabled={policyFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid
            align="flex-start"
            gutter={"xs"}
            mb="xs"
            style={{ display: "flex", alignItems: "flex-end" }}
          >
            <Grid.Col span={8}>
              <CTextarea
                name="description"
                control={control}
                label="Description"
                autosize
                minRows={2}
                maxRows={20}
                required
                showErrorText={false}
                disabled={policyFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="flex-start" gutter={"xs"} mb="lg">
            <Grid.Col span={8}>
              <CJsonInput
                name={`definition`}
                control={control}
                label="Definition"
                showErrorText={false}
                autosize
                minRows={20}
                disabled={policyFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </div>

        {(Object.keys(errors).length > 0 || policyFormState.error) && (
          <div style={styles.errors} id="errors">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormStateError errors={errors} />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormSubmitError error={policyFormState.error} />
            </div>
          </div>
        )}

        <Grid align="stretch" gutter={"xs"} columns={24} mt="lg" mb={"lg"}>
          <>
            {(policyFormState.actionOptions.allowedActions || []).map(
              (action: string) => {
                if (action === "save") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        fullWidth
                        type="submit"
                        color="teal"
                        aria-label="Save"
                        loading={policyFormState.submitting}
                      >
                        Save
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "cancel") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        component={Link}
                        to={`/app/policies?entityId=${entityId}`}
                        aria-label="Cancel"
                      >
                        Cancel
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "back") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        component={Link}
                        to={`/app/policies?entityId=${entityId}`}
                        aria-label="Back"
                      >
                        Back
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "edit") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Edit"
                        fullWidth
                        onClick={() => {
                          policyFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "EDIT",
                          });
                          policyFormDispatch({
                            type: "ACTION-OPTIONS",
                            value: getActionOptions(
                              "edit",
                              dataset,
                              dataset,
                              applicationState
                            ),
                          });

                          setAudit({
                            status: dataset?.status,
                            authStatus: "UNAUTH",
                            usableStatus: dataset?.usableStatus,
                            makerId: applicationState.user.userId,
                            makerDate: new Date(),
                            modificationNo:
                              dataset?.authStatus === "UNAUTH"
                                ? dataset?.modificationNo
                                : (dataset?.modificationNo || 0) + 1,
                            checkerId: "",
                            checkerDate: undefined,
                          });
                        }}
                      >
                        Edit
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "auth") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Auth"
                        fullWidth
                        onClick={async () => {
                          policyFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "AUTH",
                          });

                          try {
                            let payload = {
                              ...dataset,
                              entityId,
                              metaData: {
                                action: "auth",
                              },
                            };
                            await postAdapter(entityId, payload);
                            // setError("Form", "Unable to Submit");
                            policyFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "VIEW",
                            });

                            policyFormDispatch({
                              type: "SET-SUCCESS",
                              value: "Policy authorized successfully",
                            });
                          } catch (error: any) {
                            // console.log("post catch", error);
                            policyFormDispatch({
                              type: "SET-ERROR",
                              value:
                                error?.response?.data?.message ||
                                "Unable to authorize policy",
                            });
                          }
                        }}
                      >
                        Auth
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "delete") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Delete"
                        fullWidth
                        onClick={async () => {
                          policyFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "DELETE",
                          });

                          try {
                            let payload = {
                              ...dataset,
                              entityId,
                              metaData: {
                                action: "delete",
                              },
                            };
                            await postAdapter(entityId, payload);
                            // setError("Form", "Unable to Submit");
                            policyFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "VIEW",
                            });

                            policyFormDispatch({
                              type: "SET-SUCCESS",
                              value: "Policy deleted successfully",
                            });
                          } catch (error: any) {
                            // console.log("post catch", error);
                            policyFormDispatch({
                              type: "SET-ERROR",
                              value:
                                error?.response?.data?.message ||
                                "Unable to delete policy",
                            });
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Grid.Col>
                  );
                } else {
                  return null;
                }
              }
            )}
          </>
        </Grid>

        <Audit audit={audit} />
        {policyFormState.successMessage && (
          <Success message={policyFormState.successMessage} onOk={onOk} />
        )}
      </form>
    </PolicyCVMContext.Provider>
  );
}
